import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  forwardRef,
  inject,
  Input,
  signal,
  viewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ImageService } from '../../../core/api/image.service';
import { Image } from '../../../core/models/image.model';

/**
 * CVA-компонент загрузки/редактирования списка изображений.
 *
 * - Значение контрола: `RentalImage[]` (полные объекты с `preview`/`detail`),
 *   а не `number[]` — это удобно для preview-сетки и редактирования.
 *   В родительских формах список маппится в `images_before: number[]` /
 *   `images_after: number[]` при сабмите.
 * - Загрузка файлов идёт через `ImageService.upload()` (`POST /includes/images/upload/`).
 * - Внутреннее состояние — `signal()` (Angular автоматически триггерит
 *   `OnPush`-рендер при изменении сигналов).
 * - `writeValue` НЕ вызывает `onChange` (см. контракт `ControlValueAccessor`):
 *   мы только отражаем значение в `images.set(...)`.
 * - `onChange`/`onTouched` вызываются только при пользовательских
 *   изменениях (загрузка / удаление) и при потере фокуса через input.
 */
@Component({
  selector: 'app-image-upload',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
  ],
  templateUrl: './image-upload.component.html',
  styleUrl: './image-upload.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => ImageUploadComponent),
    },
  ],
})
export class ImageUploadComponent implements ControlValueAccessor {
  // ── Публичные inputs ──────────────────────────────────────────────
  @Input() label = 'Изображения';
  @Input() accept = 'image/*';
  @Input() multiple = true;
  @Input() emptyHint = 'Нет изображений';

  // ── Внутреннее состояние (signal → OnPush-совместимо) ─────────────
  readonly images = signal<Image[]>([]);
  readonly uploading = signal(false);
  readonly disabled = signal(false);

  // ── «Под кат»: число колонок грида измеряем из реального рендера ──
  // Устойчиво к любой ширине контейнера и к изменениям в SCSS-сетке.
  readonly gridRef = viewChild<ElementRef<HTMLDivElement>>('grid');
  readonly expanded = signal(false);
  readonly columns = signal(0);

  /** Сколько картинок показывать в свёрнутом состоянии (2 строки). */
  readonly collapsedLimit = computed(() => {
    const cols = this.columns();
    return cols > 0 ? cols * 2 : 0;
  });

  /** Есть ли смысл в тоггле (картинок больше, чем в 2 строках). */
  readonly canCollapse = computed(() => {
    const limit = this.collapsedLimit();
    return limit > 0 && this.images().length > limit;
  });

  /** Картинки, которые реально рендерятся. */
  readonly displayedImages = computed(() => {
    const imgs = this.images();
    if (this.expanded() || !this.canCollapse()) return imgs;
    return imgs.slice(0, this.collapsedLimit());
  });

  /** Сколько скрыто под катом — для подписи на кнопке. */
  readonly hiddenCount = computed(() => this.images().length - this.displayedImages().length);

  // ── CVA-хуки ──────────────────────────────────────────────────────
  // Не `private`, чтобы strict-templates не ругался на использование
  // в шаблоне (например, `(blur)="onTouched()"`).
  onChange: (v: Image[]) => void = () => {};
  onTouched: () => void = () => {};

  private readonly imageService = inject(ImageService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly cdr = inject(ChangeDetectorRef);

  // ── ControlValueAccessor ──────────────────────────────────────────
  /**
   * Устанавливает значение из родительской формы.
   * ВАЖНО: НЕ вызывает `onChange` (иначе бесконечный цикл при
   * `form.patchValue` / `form.setValue`).
   */
  writeValue(value: Image[] | null): void {
    this.images.set(value ? [...value] : []);
  }

  registerOnChange(fn: (v: Image[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
    // markForCheck — на всякий случай: Angular сам отслеживает сигналы,
    // но если `disabled` поменялся извне (без emission сигнала), пушнём.
    this.cdr.markForCheck();
  }

  constructor() {
    // 1) Подключаем ResizeObserver к элементу сетки, чтобы отслеживать
    //    число колонок из реального рендера. Сетка появляется/исчезает
    //    через *ngIf, поэтому реактируем на изменение `gridRef()`:
    //    effect пересоздаёт observer при появлении/удалении грида.
    //    `allowSignalWrites: true` — пишем в `columns` из эффекта
    //    (требование Angular 18, в 19+ будет default).
    effect(
      (onCleanup) => {
        const el = this.gridRef()?.nativeElement;
        if (!el) return;
        this.updateColumns(el);
        const ro = new ResizeObserver(() => this.updateColumns(el));
        ro.observe(el);
        onCleanup(() => ro.disconnect());
      },
      { allowSignalWrites: true },
    );

    // 2) Сбрасываем `expanded` в `false`, если после загрузки/удаления
    //    картинок стало ≤ 2 строк — иначе кнопка «Свернуть» висела бы
    //    над сеткой, в которой и так всё видно.
    //    Без бесконечного цикла: `canCollapse` зависит от `images` и
    //    `collapsedLimit` (← `columns`), но НЕ от `expanded`. Сброс
    //    `expanded` не пере-инвалидирует `canCollapse` обратно в `true`.
    effect(() => {
      if (!this.canCollapse() && this.expanded()) {
        this.expanded.set(false);
      }
    }, { allowSignalWrites: true });
  }

  // ── «Под кат»: helpers ─────────────────────────────────────────────
  /** Читает `gridTemplateColumns` из computed-стилей и пишет число треков. */
  private updateColumns(grid: HTMLElement): void {
    const tracks = getComputedStyle(grid).gridTemplateColumns.split(' ').filter(Boolean);
    this.columns.set(tracks.length || 0);
  }

  /** Тоггл разворота/сворачивания ката. */
  toggleExpanded(): void {
    this.expanded.update((v) => !v);
  }

  // ── UI handlers ───────────────────────────────────────────────────
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    // Сбрасываем value, чтобы повторный выбор того же файла триггернул change
    const reset = () => {
      input.value = '';
    };
    if (!input.files || input.files.length === 0) {
      reset();
      return;
    }
    if (this.disabled()) {
      reset();
      return;
    }

    const files = Array.from(input.files);
    // Дополнительная клиентская фильтрация — на случай, если `accept`
    // обойдён (drag&drop в сторонних килентах).
    const imageFiles = files.filter((f) => f.type.startsWith('image/'));
    if (imageFiles.length === 0) {
      this.snackBar.open('Выберите файлы изображений', 'ОК', { duration: 3000 });
      reset();
      return;
    }

    this.uploading.set(true);
    this.imageService.upload(imageFiles).subscribe({
      next: (uploaded) => {
        if (uploaded.length > 0) {
          this.images.update((current) => [...current, ...uploaded]);
          this.onChange([...this.images()]);
        }
        this.uploading.set(false);
        this.onTouched();
        reset();

        if (files.length !== imageFiles.length) {
          this.snackBar.open(
            'Часть файлов не является изображениями и была пропущена',
            'ОК',
            { duration: 3000 },
          );
        }
      },
      error: () => {
        this.uploading.set(false);
        this.snackBar.open('Не удалось загрузить изображения', 'ОК', { duration: 5000 });
        reset();
      },
    });
  }

  removeImage(index: number, event: Event): void {
    event.stopPropagation();
    if (this.disabled()) return;
    this.images.update((current) => current.filter((_, i) => i !== index));
    this.onChange([...this.images()]);
    this.onTouched();
  }

  openImage(url: string): void {
    // Простое и надёжное открытие детального изображения в новой вкладке.
    // Альтернатива — MatDialog с увеличенной картинкой, но `window.open`
    // проще и не требует доп. DI.
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  triggerFileInput(fileInput: HTMLInputElement): void {
    if (this.disabled() || this.uploading()) return;
    fileInput.click();
  }

  /** Сеттер для `(blur)="markTouched()"` в шаблоне. */
  markTouched(): void {
    this.onTouched();
  }
}
